import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { routerNavigatedAction } from '@ngrx/router-store';
import { Action, createReducer, on } from '@ngrx/store';
import { last } from 'lodash';

import {
  ClientActions,
  ClientEnquiriesActions,
} from '@lockton-apps/clients/actions';
import {
  DashboardBrokerActions,
  DashboardBrokerNorwayActions,
  DashboardContactActions,
} from '@lockton-apps/dashboard/action';
import {
  LsosRenewalsApplicationActions,
  LsosRenewalsOverviewActions,
} from '@lockton-apps/lsos-renewals/action';
import { PolicyDetailsActions } from '@lockton-apps/policies/action';
import { ProposalShellActions } from '@lockton-apps/proposals/action';
import {
  QuoteDetailsActions,
  QuoteOverviewActions,
} from '@lockton-apps/quotes/action';
import { EndorsementsSharedActions } from '@lockton-apps/shared/action/endorsements';
import { QuotesApiActions } from '@lockton-apps/shared/action/quotes';
import { Quote } from '@lockton-apps/shared/interfaces';
import {
  EnquiryOverviewActions,
  NewClientEnquiryActions,
  NewEnquiryActions,
  UpdateEnquiryActions,
} from '@lockton-apps/enquiries/action';
import { LocalActions } from './actions';

export const QUOTES_FEATURE_KEY = 'quotes';

export interface QuotesState extends EntityState<Quote> {
  selectedId?: number | null; // which Quotes record has been selected
  loaded: boolean; // has the Quotes list been loaded
  updating?: boolean;
  error?: string | null; // last known error (if any)
}

export interface quotesPartialState {
  readonly [QUOTES_FEATURE_KEY]: QuotesState;
}

export const quotesAdapter: EntityAdapter<Quote> = createEntityAdapter<Quote>();

export const initialQuotesState: QuotesState = quotesAdapter.getInitialState({
  // set initial required properties
  loaded: false,
});

const reducer = createReducer(
  initialQuotesState,
  on(QuotesApiActions.loadQuotesSuccess, (state, { quotes }) => {
    return quotesAdapter.setAll(quotes, {
      ...state,
      loaded: true,
    });
  }),
  on(
    LsosRenewalsOverviewActions.init,
    PolicyDetailsActions.init,
    DashboardContactActions.init,
    QuoteDetailsActions.init,
    DashboardBrokerNorwayActions.init,
    DashboardBrokerActions.init,
    ProposalShellActions.init,
    ClientEnquiriesActions.init,
    UpdateEnquiryActions.init,
    NewClientEnquiryActions.init,
    ClientActions.init,
    ClientActions.clientSelected,
    (state) => ({
      ...state,
      loaded: false,
      error: null,
    })
  ),
  on(
    LsosRenewalsOverviewActions.loadQuotesSuccess,
    LsosRenewalsApplicationActions.loadQuotesSuccess,
    (state, { quotes }) => {
      const lastQuote = last(quotes);
      return quotesAdapter.setAll(quotes, {
        ...state,
        selectedId: lastQuote ? lastQuote.id : null,
        loaded: true,
      });
    }
  ),
  on(
    LsosRenewalsOverviewActions.loadEmptyQuotesSuccess,
    LsosRenewalsApplicationActions.loadEmptyQuotesSuccess,
    (state) => ({
      ...state,
      selectedId: null,
      loaded: true,
    })
  ),
  on(
    LsosRenewalsOverviewActions.loadQuotesFailure,
    LsosRenewalsApplicationActions.loadQuotesFailure,
    QuotesApiActions.loadQuotesForContactDashboardFailure,
    QuotesApiActions.proposalReturnedFailure,
    QuotesApiActions.holdFailure,
    QuotesApiActions.sendToClientFailure,
    QuotesApiActions.acceptFailure,
    QuotesApiActions.invoiceFailure,
    QuotesApiActions.paymentReceivedFailure,
    QuotesApiActions.completeFailure,
    QuotesApiActions.reconfigured2Failure,
    QuotesApiActions.proposalSubmittedFailure,
    (state, { error }) => ({
      ...state,
      updating: false,
      error,
    })
  ),
  on(LsosRenewalsApplicationActions.setNewQuoteId, (state, { quoteId }) => ({
    ...state,
    selectedId: quoteId,
  })),
  on(
    QuotesApiActions.loadQuoteSuccess,
    QuotesApiActions.loadQuoteWithPropFormSuccess,
    QuotesApiActions.proposalReturnedSuccess,
    QuotesApiActions.holdSuccess,
    QuotesApiActions.sendToClientSuccess,
    QuotesApiActions.acceptSuccess,
    QuotesApiActions.invoiceSuccess,
    QuotesApiActions.paymentReceivedSuccess,
    QuotesApiActions.completeSuccess,
    QuotesApiActions.reconfigured2Success,
    QuotesApiActions.adjustmentsAppliedSuccess,
    QuotesApiActions.contactsAssignedSuccess,
    (state, { quote }) =>
      quotesAdapter.upsertOne(quote, {
        ...state,
        selectedId: quote.id,
        loaded: true,
        updating: false,
      })
  ),
  on(
    DashboardBrokerActions.brokerAssignedToQuote,
    ClientEnquiriesActions.brokerAssigned,
    (state, { quoteId, user }) => {
      return quotesAdapter.mapOne(
        {
          id: quoteId,
          map: (quote) => ({
            ...quote,
            assignedUserId: user ? user.userId : null,
            assignedUserName: user ? user.displayName : null,
          }),
        },
        {
          ...state,
        }
      );
    }
  ),
  on(
    QuotesApiActions.loadClientQuotesSuccess,
    QuotesApiActions.loadClientQuotesWithLiveCheckSuccess,
    QuotesApiActions.loadQuotesForContactDashboardSuccess,

    (state, { quotes }) =>
      quotesAdapter.setAll(quotes, {
        ...state,
        loaded: true,
      })
  ),
  on(
    QuoteDetailsActions.quoteCompleted,
    QuoteDetailsActions.reQuoted,
    QuoteOverviewActions.continueClicked,
    EndorsementsSharedActions.endorsementDialogConfirmed,
    NewEnquiryActions.formSubmitted,
    EnquiryOverviewActions.quoteStatusUpdated,
    EnquiryOverviewActions.acceptQuote,
    EnquiryOverviewActions.invoice,
    LocalActions.sendRenewalInviteDialogConfirmed,
    LocalActions.returnDialogConfirmed,
    LocalActions.holdDialogConfirmed,
    LocalActions.sendToClientDialogConfirmed,
    LocalActions.paymentReceivedDialogConfirmed,
    LocalActions.submitProposalConfirmed,
    LocalActions.proposalBackupSaved,
    (state) => ({
      ...state,
      updating: true,
    })
  ),
  on(
    QuotesApiActions.quoteApprovedSuccess,
    QuotesApiActions.requotedSuccess,
    QuotesApiActions.updateStatusSuccess,
    QuotesApiActions.checklistUpdatedFailure,
    QuotesApiActions.sendRenewalInviteSuccess,
    QuotesApiActions.proposalUpdatedSuccess,
    QuotesApiActions.proposalSubmittedSuccess,
    QuotesApiActions.proposalBackupSavedSuccess,
    QuotesApiActions.quoteRequestedSuccess,
    QuotesApiActions.quoteReadySuccess,
    QuotesApiActions.notTakenUpSuccess,
    QuotesApiActions.setPaymentTypeSuccess,
    QuotesApiActions.endorsementSubmittedSuccess,
    (state, { quote }) =>
      quotesAdapter.upsertOne(quote, {
        ...state,
        updating: false,
      })
  ),
  on(
    QuotesApiActions.completedSuccess,
    QuotesApiActions.initialisedSuccess,
    QuotesApiActions.reconfiguredSuccess,
    (state, { quotes }) =>
      quotesAdapter.upsertMany(quotes, {
        ...state,
        updating: false,
      })
  ),
  on(
    QuotesApiActions.quoteApprovedFailure,
    QuotesApiActions.requotedFailure,
    QuotesApiActions.completedFailure,
    QuotesApiActions.updateStatusFailure,
    QuotesApiActions.initialisedFailure,
    QuotesApiActions.reconfiguredFailure,
    QuotesApiActions.setPaymentTypeFailure,
    QuotesApiActions.tierSelectedFailure,
    QuotesApiActions.adjustmentsAppliedFailure,
    QuotesApiActions.addonToggledFailure,
    QuotesApiActions.addonAdjustedFailure,
    QuotesApiActions.sendRenewalInviteFailure,
    QuotesApiActions.proposalUpdatedFailure,
    QuotesApiActions.proposalAutoSavedFailure,
    QuotesApiActions.proposalBackupSavedFailure,
    QuotesApiActions.endorsementSubmittedFailure,
    (state, { error }) => ({
      ...state,
      error,
      updating: false,
    })
  ),
  on(
    QuotesApiActions.tierAdjustedSuccess,
    NewEnquiryActions.formSubmitCancelled,
    routerNavigatedAction,
    (state) => ({
      ...state,
      updating: false,
    })
  )
);

export function quotesReducer(state: QuotesState | undefined, action: Action) {
  return reducer(state, action);
}
